import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Badge,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VuiPageTitle from "../../../components/page-title";
import { useTranslation } from "react-i18next";
import VuiButton from "../../../components/button";
import {
  GoogleOutlined,
  MailOutlined,
  PlusOutlined,
  RetweetOutlined,
  SearchOutlined,
  TrademarkCircleOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table/interface";
import { InvoiceDataList } from "./interface";
import VuiTableActionButton from "../../../components/table-action-button";
import { useGetInvoice } from "../hooks/use-get-invoice";
import VuiAccessible from "../../../components/accessible";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import { overrideTableSortOrder, uiFilterData } from "../../../utils/helpers";
import VuiSelect from "../../../components/select";
import { ValueType } from "../../../components/select/interface";
import {
  DEFAULT_PAGE_SIZE,
  INVOICE_STATUS_COLOR,
  LOCALSTORAGE_FILTER_DATA_KEY,
} from "../../../constants";
import _ from "lodash";
import useDebounce from "../../../hooks/use-debounce";
import CustomerRepository from "../../../repositories/customer-repository";
import UserRepository from "../../../repositories/user-repository";
import moment from "moment";
import ConstantRepository from "../../../repositories/constant-repository";
import { useAuth } from "../../../context/auth";
import TabPane from "antd/lib/tabs/TabPane";
import VuiNumberFormat from "../../../components/number-format";
import { useExportInvoice } from "../hooks/use-export-invoice";
import DateRangePicker from "../../../components/date-range-picker";
import GoogleIcon from "../../../assets/icons/google-icon";
import { useInvoiceCount } from "../../../context/invoice";

const qs = require("qs-lite");

const { Text } = Typography;

type InvoiceTabType = "default" | "approval" | "scheduled";

const tabs: InvoiceTabType[] = ["default", "approval", "scheduled"];

const InvoiceListModule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loadData, isOnFetchingData, totalData } = useGetInvoice();
  const { onExport, downloadLoading } = useExportInvoice();
  const { statusCounts } = useInvoiceCount();
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<InvoiceTabType>(
    (searchParams.get("tab") as InvoiceTabType) || "default"
  );

  const canApprove = useMemo(() => {
    return !!state.user?.permissions.includes("invoice.can_approve");
  }, [state.user?.permissions]);

  const [form] = Form.useForm();

  const [selectedCustomer, setSelectedCustomer] = useState<
    ValueType | undefined
  >();

  const [selectedProjectManager, setSelectedProjectManager] = useState<
    ValueType | undefined
  >();

  const [selectedStatus, setSelectedStatus] = useState<ValueType | undefined>();

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);

  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy"),
    order_by: searchParams.get("orderBy") as string,
    with: ["projectManager", "contacts"],
  });

  const exportPermissionOptions = useMemo(() => {
    const options = [];

    if (state?.user?.permissions.includes("export.sales-invoice")) {
      options.push({
        label: "Sales Invoice",
        value: "sales-invoice",
      });
    }

    if (state?.user?.permissions.includes("export.sales-payment")) {
      options.push({
        label: "Sales Payment",
        value: "sales-payment",
      });
    }

    return options;
  }, []);

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    if (selectedCustomer) {
      Object.assign(params, {
        customer: selectedCustomer.value,
      });
    } else {
      const recentCustomer = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.customer
      );
      if (!recentCustomer) {
        searchParams.delete("customer");
      } else {
        const paramsCustomerIds = _.map(
          searchParams.getAll("customer"),
          (item) => parseInt(item)
        );

        if (paramsCustomerIds.length) {
          const customerFromStorage = uiFilterData.getSelectedFilter(
            recentCustomer,
            paramsCustomerIds
          );

          setSelectedCustomer(customerFromStorage[0]);

          Object.assign(params, {
            customer: customerFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.customer);
        }
      }
    }

    if (selectedProjectManager) {
      Object.assign(params, {
        project_manager: selectedProjectManager.value,
      });
    } else {
      const recentProjectManager = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.projectManager
      );
      if (!recentProjectManager) {
        searchParams.delete("project_manager");
      } else {
        const paramsProjectManagerIds = _.map(
          searchParams.getAll("project_manager"),
          (item) => parseInt(item)
        );

        if (paramsProjectManagerIds.length) {
          const projectManagerFromStorage = uiFilterData.getSelectedFilter(
            recentProjectManager,
            paramsProjectManagerIds
          );

          setSelectedProjectManager(projectManagerFromStorage[0]);

          Object.assign(params, {
            project_manager: projectManagerFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.projectManager);
        }
      }
    }

    if (selectedStatus) {
      Object.assign(params, {
        status: selectedStatus.value,
      });
    } else {
      const recentStatus = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.status
      );
      if (!recentStatus) {
        searchParams.delete("status");
      } else {
        const paramsStatusIds = _.map(searchParams.getAll("status"), (item) =>
          parseInt(item)
        );

        if (paramsStatusIds.length) {
          const statusFromStorage = uiFilterData.getSelectedFilter(
            recentStatus,
            paramsStatusIds
          );

          setSelectedStatus(statusFromStorage[0]);

          Object.assign(params, {
            status: statusFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.status);
        }
      }
    }

    if (canApprove) {
      Object.assign(params, {
        tab: selectedTab,
      });
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/invoice");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.order_by ? { order_by: tableQuery.order_by } : {}),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : {}),
      ...(params.customer ? { customer: params.customer } : {}),
      ...(params.project_manager
        ? { project_manager: params.project_manager }
        : {}),
      ...(params.status ? { status: params.status } : {}),
      for_approval: selectedTab === "approval",
      for_scheduled: selectedTab === "scheduled",
      for_all: selectedTab === "default",
      with: tableQuery.with,
    };

    await loadData(tableParams);

    setHasInitialize(true);
  }, [
    tableQuery,
    debouncedSearch,
    selectedCustomer,
    selectedProjectManager,
    selectedStatus,
    loadData,
    navigate,
    searchParams,
    setSearchParams,
    selectedTab,
    canApprove,
  ]);

  const columns: ColumnsType<InvoiceDataList> = [
    {
      title: t("common.text.date"),
      dataIndex: "date",
      key: "date",
      width: 100,
      render: (text) => <Text>{moment(text).format("D MMM YYYY")}</Text>,
    },
    {
      title:
        selectedTab === "default" || selectedTab === "scheduled"
          ? t("common.text.number")
          : t("common.text.request"),
      dataIndex:
        selectedTab === "default" || selectedTab === "scheduled"
          ? "name"
          : "request_by",
      key:
        selectedTab === "default" || selectedTab === "scheduled"
          ? "name"
          : "request_by",
      render: (text, record) =>
        selectedTab === "default" || selectedTab === "scheduled" ? (
          <Space size="small" align="center">
            {text}
            {record.recurring_setting_id && (
              <RetweetOutlined style={{ color: "#BFBFBF" }} />
            )}
            {record.gw_customer_id && (
              <GoogleIcon
                width={13}
                height={13}
                viewBox="0 0 24 24"
                color="#BFBFBF"
                style={{ verticalAlign: "middle" }}
              />
            )}
          </Space>
        ) : (
          text
        ),
    },
    {
      title: t("common.text.client"),
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      render: (text, record) => (
        <Space>
          <Text>{text}</Text>
          <Tag style={{ color: "#757575" }}>
            {record.project_manager?.initial}
          </Tag>
        </Space>
      ),
    },
    {
      title: t("common.text.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      render: (text) => <Text>{moment(text).format("D MMM YYYY")}</Text>,
    },
    {
      title: `${t("common.text.amount")} (Rp)`,
      dataIndex: "total",
      key: "total",
      render: (text) => <VuiNumberFormat value={text} />,
    },
    {
      title: t("common.text.status"),
      dataIndex: "status_name",
      key: "status_name",
      render: (text) => (
        <Tag color={_.get(INVOICE_STATUS_COLOR, text, "blue")}>{text}</Tag>
      ),
    },
    {
      title: t("common.text.sentDate"),
      dataIndex: "sent_date",
      key: "sent_date",
      render: (text) => (
        <Text>{text ? moment(text).format("D MMM YYYY | HH:mm") : "-"}</Text>
      ),
    },
    {
      title: t("common.text.sentTo"),
      dataIndex: "sent_to",
      key: "sent_to",
      width: 150,
      render: (_, record) => {
        const emails = record.contacts
          .filter(({ email }) => email)
          .map(({ email }) => email);

        const phones = record.contacts
          .filter(({ phone }) => phone)
          .map(({ phone }) => `0${phone}`);

        return (
          <Space direction="horizontal" wrap={false}>
            {emails.length > 0 && (
              <Tooltip title={emails.join(", ")}>
                <Tag color="#55acee" icon={<MailOutlined />}>
                  Email
                </Tag>
              </Tooltip>
            )}
            {phones.length > 0 && (
              <Tooltip title={phones.join(", ")}>
                <Tag color="#25D366" icon={<WhatsAppOutlined />}>
                  WhatsApp
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 50,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/invoice/${record.id}`}
            detailPermissionName="invoice.show"
            showDetail={record?.can_show}
            openNewTab={true}
          />
        );
      },
    },
  ];

  const handleSubmitExport = async (values: any) => {
    const { type, date } = values;
    const [start_date, end_date] = date;

    await onExport(type, {
      start_date: start_date.format("YYYY-MM-DD"),
      end_date: end_date.format("YYYY-MM-DD"),
    });
  };

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
        with: tableQuery.with,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );

  const handleOnChangeCustomer = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.customer, [value]);
    setSelectedCustomer(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      customer: value,
    }));
  }, []);

  const handleOnChangeProjectManager = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.projectManager, [value]);
    setSelectedProjectManager(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      project_manager: value,
    }));
  }, []);

  const handleOnChangeStatus = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.status, [value]);
    setSelectedStatus(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      status: value,
    }));
  }, []);

  const handleTabChange = (key: string) => {
    setSelectedTab(key as InvoiceTabType);
    setSearch("");
    handleOnChangeCustomer(undefined as unknown as ValueType);
    handleOnChangeProjectManager(undefined as unknown as ValueType);
    handleOnChangeStatus(undefined as unknown as ValueType);

    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      tab: key,
    }));
  };

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const renderTabContent = () => {
    const tableColumns = columns.filter((col) => {
      if (["default", "approval"].includes(selectedTab)) {
        return ![
          "sent_date",
          "sent_to",
          ...(selectedTab === "approval" ? ["status_name"] : []),
        ].includes(col.key as string);
      }
      if (selectedTab === "scheduled") {
        return !["status_name", "due_date"].includes(col.key as string);
      }
      return true;
    });

    return (
      <Table
        rowKey="id"
        columns={tableColumns}
        dataSource={data}
        loading={isOnFetchingData}
        onChange={handleTableChange}
        pagination={{
          current: tableQuery.page,
          showSizeChanger: true,
          pageSize: tableQuery.per_page,
          total: totalData,
        }}
        size="small"
        scroll={{ x: "max-content" }}
      />
    );
  };

  return (
    <>
      <VuiPageTitle title={t("pages.invoice.list.title")}>
        <VuiAccessible
          access={["export.sales-invoice", "export.sales-payment"]}
        >
          <Button type="primary" ghost onClick={() => setShowExportModal(true)}>
            Export
          </Button>
        </VuiAccessible>
        <VuiAccessible access="invoice.store">
          <Link to="/invoice/add">
            <VuiButton
              title={t("common.button.requestItem", {
                item: t("common.text.invoice"),
              })}
              buttonProps={{
                icon: <PlusOutlined />,
                size: "middle",
              }}
            />
          </Link>
        </VuiAccessible>
      </VuiPageTitle>

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            size="middle"
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={CustomerRepository}
            value={selectedCustomer}
            placeholder={t("common.text.selectClient")}
            onChange={handleOnChangeCustomer}
            className="select-filter"
            size="middle"
          />
        </Col>
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={UserRepository}
            value={selectedProjectManager}
            placeholder={t("common.text.selectProjectManager")}
            onChange={handleOnChangeProjectManager}
            className="select-filter"
            size="middle"
          />
        </Col>
        {selectedTab === "default" && (
          <Col className="gutter-row" xs={24} md={4}>
            <VuiSelect
              repository={ConstantRepository}
              value={selectedStatus}
              placeholder={t("common.text.selectStatus")}
              onChange={handleOnChangeStatus}
              repositoryParams={{
                for: "status_invoice",
              }}
              labelKey="label"
              className="select-filter"
              size="middle"
            />
          </Col>
        )}
      </Row>

      {canApprove ? (
        <Tabs defaultActiveKey={selectedTab} onChange={handleTabChange}>
          {tabs.map((tab) => {
            let tabLabel = tab === "default" ? "All" : tab === "approval" ? "Waiting for Approval" : "Scheduled";

            if (tab === "approval" && statusCounts.waiting_for_approval > 0) {
              return (
                <TabPane
                  key={tab}
                  tab={
                    <Space align="center" size={5}>
                      <Badge
                        count={statusCounts.waiting_for_approval}
                        style={{ backgroundColor: '#FAAC14', marginBottom: "2px" }}
                        overflowCount={99}
                        size="small"
                        offset={[0, 0]}
                      />
                      {tabLabel}
                    </Space>
                  }
                >
                  {renderTabContent()}
                </TabPane>
              );
            }
            
            if (tab === "scheduled" && statusCounts.scheduled > 0) {
              return (
                <TabPane
                  key={tab}
                  tab={
                    <Space align="center" size={5}>
                      <Badge
                        count={statusCounts.scheduled}
                        style={{ backgroundColor: '#0078F0', marginBottom: "2px" }}
                        overflowCount={99}
                        size="small"
                        offset={[0, 0]}
                      />
                      {tabLabel}
                    </Space>
                  }
                >
                  {renderTabContent()}
                </TabPane>
              );
            }

            return (
              <TabPane tab={tabLabel} key={tab}>
                {renderTabContent()}
              </TabPane>
            );
          })}
        </Tabs>
      ) : (
        renderTabContent()
      )}

      <Modal
        width={600}
        open={showExportModal}
        onCancel={() => setShowExportModal(false)}
        title={t("common.text.exportInvoice")}
        footer={
          <>
            <Button htmlType="button" onClick={() => setShowExportModal(false)}>
              {t("common.button.cancel")}
            </Button>
            <Button
              onClick={() => form.submit()}
              type="primary"
              loading={downloadLoading}
            >
              {t("common.button.submit")}
            </Button>
          </>
        }
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={{ date: [moment().subtract(1, "month"), moment()] }}
          onFinish={handleSubmitExport}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={10}>
              <Form.Item
                name="type"
                label={t("common.form.type.label")}
                rules={[
                  {
                    required: true,
                    message: t("validation.required", {
                      item: t("common.form.type.label"),
                    }),
                  },
                ]}
              >
                <Select
                  size="large"
                  placeholder={t("common.form.type.placeholder")}
                  options={exportPermissionOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <Form.Item
                name="date"
                initialValue={moment()}
                label={t("common.form.dateRange.label")}
                rules={[
                  {
                    required: true,
                    message: t("validation.required", {
                      item: t("common.form.dateRange.label"),
                    }),
                  },
                ]}
              >
                <DateRangePicker />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceListModule;
